/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// BIS Form Integration for Focal Theme

function setupStickyFormIntegration() {
  const stickyForm = document.querySelector('product-sticky-form');
  const mainBisForm = document.querySelector('.klaviyo-bis-custom');
  
  if (!stickyForm || !mainBisForm) return;
  
  // Replace the add to cart button with "Get Notified" when out of stock
  function updateStickyButton(variant) {
    const stickyButton = stickyForm.querySelector('button[type="submit"]');
    const stickyContainer = stickyForm.querySelector('.product-sticky-form__payment-container');
    
    if (!variant || !variant.available) {
      // Replace with notification button
      stickyContainer.innerHTML = `
        <button type="button" class="button button--primary" id="sticky-notify-btn">
          Sold Out - Get Notified
        </button>
      `;
      
      // Add click handler to scroll to main form
      const notifyBtn = stickyContainer.querySelector('#sticky-notify-btn');
      notifyBtn.addEventListener('click', function() {
        // Scroll to main form
        mainBisForm.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Focus email input after scroll completes
        setTimeout(() => {
          const emailInput = mainBisForm.querySelector('#BISForm-email');
          if (emailInput) {
            emailInput.focus();
            // Optional: Add subtle highlight animation
            mainBisForm.style.animation = 'pulse 0.5s ease-in-out';
          }
        }, 500);
      });
    } else {
      // Show normal add to cart (let theme handle this)
      // The theme's variant change will restore the original button
    }
  }
  
  // Listen for variant changes
  document.addEventListener('variant:change', function(event) {
    updateStickyButton(event.detail.variant);
  });
  
  // Initial check
  const variantPicker = document.querySelector('variant-picker');
  if (variantPicker && variantPicker.selectedVariant) {
    updateStickyButton(variantPicker.selectedVariant);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  
  // Find all BIS forms and set up variant change listeners
  document.querySelectorAll('.klaviyo-bis-custom').forEach(bisForm => {
    const productFormElement = bisForm.closest('form[is="product-form"]') || 
                              document.querySelector('form[is="product-form"]');
    
    if (!productFormElement) return;
    
    console.log('BIS: Setting up form integration');
    
    // Reset the BIS form state initially
    resetBISForm(bisForm);
    
    // Listen to Focal's variant change events
    productFormElement.addEventListener('variant:change', function(event) {
      handleBISVariantChange(bisForm, event.detail.variant, event.detail.previousVariant);
    });
    
    // Also listen for variant:changed event as backup
    productFormElement.addEventListener('variant:changed', function(event) {
      handleBISVariantChange(bisForm, event.detail.variant, event.detail.previousVariant);
    });
    
    // Initial check on page load
    initialBISCheck(bisForm, productFormElement);
  });
  
  // Handle variant changes for BIS form
  function handleBISVariantChange(bisForm, variant, previousVariant) {
    if (!bisForm) return;
    
    //console.log('BIS: Variant changed', { variant, previousVariant });
    
    // Reset the BIS form state first
    resetBISForm(bisForm);
    
    // Update BIS form variant ID
    const bisVariantInput = bisForm.querySelector('.bis-form input[name="variant_id"]');
    const bisProductTitleInput = bisForm.querySelector('.bis-form input[name="product_title"]');
    
    if (bisVariantInput && variant?.id) {
      bisVariantInput.value = variant.id;
    }
    
    // Update BIS form product title
    if (bisProductTitleInput && variant) {
      // Get product title from the page
      const productTitle = document.querySelector('h1.product-title, .product-meta h1, h1')?.textContent?.trim() || 
                          document.querySelector('[data-product-title]')?.textContent?.trim();
      
      const variantTitle = variant.title || variant.name || 'Default Title';
      const newProductTitle = `${productTitle} - ${variantTitle}`;
      bisProductTitleInput.value = newProductTitle;
      //console.log('BIS: Updated product title to:', newProductTitle);
    }
    
    // Show/hide BIS form based on variant availability
    if (variant && variant.available === false) {
      bisForm.style.display = 'block';
      //console.log('BIS: Showing form - variant not available');
    } else {
      bisForm.style.display = 'none';
      //console.log('BIS: Hiding form - variant available');
    }
  }
  
  // Reset BIS form to initial state
  function resetBISForm(bisForm) {
    const form = bisForm.querySelector('.bis-form');
    if (!form) return;
    
    // Reset form fields
    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]');
    
    // Hide all messages
    const successMsg = form.querySelector('.bis-success');
    const errorMsg = form.querySelector('.bis-error');
    const emailError = form.querySelector('.bis-email-error');
    
    if (successMsg) successMsg.style.display = 'none';
    if (errorMsg) errorMsg.style.display = 'none';
    if (emailError) emailError.style.display = 'none';
    
    // Reset email input
    if (emailInput) {
      emailInput.value = '';
      emailInput.removeAttribute('aria-invalid');
      emailInput.removeAttribute('aria-describedby');
    }
    
    // Reset button state
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = 'Notify Me When Available';
      submitButton.classList.remove('loading');
    }
    
    // Show form fields again
    const fieldDiv = form.querySelector('.field');
    const buttonDiv = form.querySelector('.contact__button');
    
    if (fieldDiv) fieldDiv.style.display = 'block';
    if (buttonDiv) buttonDiv.style.display = 'block';
    
    console.log('BIS: Form reset');
  }
  
  // Initial check on page load
  function initialBISCheck(bisForm, productFormElement) {
    if (!bisForm) return;
    
    // Reset form on initial load
    resetBISForm(bisForm);
    
    // Get the current variant from the variant picker
    const variantPicker = productFormElement.querySelector('variant-picker');
    if (variantPicker && variantPicker.selectedVariant) {
      const variant = variantPicker.selectedVariant;
      
      // Update product title on initial load
      const bisProductTitleInput = bisForm.querySelector('.bis-form input[name="product_title"]');
      if (bisProductTitleInput) {
        const productTitle = document.querySelector('h1.product-title, .product-meta h1, h1')?.textContent?.trim();
        const variantTitle = variant.title || variant.name || 'Default Title';
        const newProductTitle = `${productTitle} - ${variantTitle}`;
        bisProductTitleInput.value = newProductTitle;
        //console.log('BIS: Initial product title set to:', newProductTitle);
      }
      
      // Show/hide based on availability
      if (variant.available === false) {
        bisForm.style.display = 'block';
        //console.log('BIS: Initial check - showing form (not available)');
      } else {
        bisForm.style.display = 'none';
        //console.log('BIS: Initial check - hiding form (available)');
      }
    }
  }
  setupStickyFormIntegration();
  //console.log('BIS: Integration loaded for Focal theme');
});